import React, { useState, useRef } from "react";
import styles from "./BookingSection.module.css";

function BookingSection({ config = {} }) {
  const labels = config.labels || {};
  const helpOptions = config.helpAreas || defaultHelpAreas;

  const [step, setStep] = useState(1);
  const [submitted, setSubmitted] = useState(false);
  const formRef = useRef(null);

  const [formData, setFormData] = useState({
    business: "",
    website: "",
    industry: "",
    email: "",
    phone: "",
    contactMethod: "",
    years: "",
    teamSize: "",
    revenue: "",
    helpAreas: [],
    painPoint: "",
    dream: "",
    lifeChange: ""
  });

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (type === "checkbox" && name === "helpAreas") {
      const updated = checked
        ? [...formData.helpAreas, value]
        : formData.helpAreas.filter((v) => v !== value);
      setFormData({ ...formData, helpAreas: updated });
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const canContinue = () => {
    if (step === 1) {
      return (
        formData.business &&
        formData.website &&
        formData.industry &&
        formData.email &&
        formData.phone &&
        formData.contactMethod
      );
    }
    if (step === 2) {
      return (
        formData.years &&
        formData.teamSize &&
        formData.revenue &&
        formData.helpAreas.length > 0
      );
    }
    if (step === 3) {
      return formData.painPoint && formData.dream && formData.lifeChange;
    }
    return false;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const formDataToSend = new FormData();

    Object.entries(formData).forEach(([key, value]) => {
  if (Array.isArray(value)) {
    value
      .filter(val => val && val.trim() !== "")
      .forEach(val => formDataToSend.append(key, val));
  } else if (value && value.trim() !== "") {
    formDataToSend.append(key, value);
  }
});


    try {
      const response = await fetch("https://formspree.io/f/meokzdpg", {
        method: "POST",
        body: formDataToSend,
        headers: {
          Accept: "application/json"
        }
      });

      const result = await response.json();

      if (response.ok) {
        setSubmitted(true);
        setTimeout(() => {
          document.querySelector(`.${styles.thankYouWrapper}`)?.scrollIntoView({ behavior: "smooth" });
        }, 100);
      } else {
        console.error("Formspree response error:", result);
        alert("There was a problem submitting your form. Please try again later.");
      }
    } catch (err) {
      console.error("Network error:", err);
      alert("Failed to submit. Please check your connection and try again.");
    }
  };

  const renderStepDots = () => (
    <div className={styles.dots}>
      {[1, 2, 3].map((s) => (
        <span key={s} className={`${styles.dot} ${step === s ? styles.activeDot : ""}`} />
      ))}
    </div>
  );

  return (
    <>
      <section className={`${styles.wrapper} ${submitted ? styles.hidden : ""}`} id="book">
        <h2 className={styles.heading}>
          Take the Tailored Approach. Let’s Design Your Next Level Strategy.
        </h2>
        <p className={styles.subtext}>
          Tell us what you want to create, change, or scale.
          <br />
          We’ll send you a custom plan. Zero pressure. Just clarity. Replies within 48 hours.
        </p>

        <form ref={formRef} className={styles.form} onSubmit={handleSubmit}>
          {step === 1 && (
            <div className={styles.formStep}>
              <div className={styles["grid-3x3"]}>
                <input name="business" placeholder={labels.business || "Business or Brand"} value={formData.business} onChange={handleChange} />
                <input name="website" placeholder={labels.website || "Website or Socials"} value={formData.website} onChange={handleChange} />
                <input name="industry" placeholder={labels.industry || "Industry/Niche"} value={formData.industry} onChange={handleChange} />
                <input name="email" placeholder="Email" value={formData.email} onChange={handleChange} />
                <input name="phone" placeholder="Phone" value={formData.phone} onChange={handleChange} />
                <select name="contactMethod" value={formData.contactMethod} onChange={handleChange}>
                  <option value="">Preferred Contact Method</option>
                  <option value="email">Email</option>
                  <option value="phone">Phone</option>
                  <option value="text">Text</option>
                </select>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className={styles.formStep}>
              <div className={styles["grid-3x3"]}>
                <select name="years" value={formData.years} onChange={handleChange}>
                  <option value="">Years in Business</option>
                  <option value="0-1">0-1</option>
                  <option value="1-3">1-3</option>
                  <option value="3+">3+</option>
                </select>
                <select name="teamSize" value={formData.teamSize} onChange={handleChange}>
                  <option value="">Team Size</option>
                  <option value="1-3">1-3</option>
                  <option value="4-10">4-10</option>
                  <option value="10+">10+</option>
                </select>
                <select name="revenue" value={formData.revenue} onChange={handleChange}>
                  <option value="">{labels.revenue || "Monthly Revenue"}</option>
                  <option value="<5k">Under $5k</option>
                  <option value="5k-20k">$5k–$20k</option>
                  <option value="20k+">$20k+</option>
                </select>
              </div>
              <fieldset className={styles.helpAreas}>
                <legend>Where do you want the most help?</legend>
                {helpOptions.map((area) => (
                  <label key={area}>
                    <input
                      type="checkbox"
                      name="helpAreas"
                      value={area}
                      checked={formData.helpAreas.includes(area)}
                      onChange={handleChange}
                    />
                    {area}
                  </label>
                ))}
              </fieldset>
            </div>
          )}

          {step === 3 && (
            <div className={styles.textInputs}>
              <textarea
                name="painPoint"
                placeholder={labels.painPoint || "What’s your #1 business frustration right now?"}
                value={formData.painPoint}
                onChange={handleChange}
              />
              <textarea
                name="dream"
                placeholder={labels.dream || "What would your business look like in 6–12 months?"}
                value={formData.dream}
                onChange={handleChange}
              />
              <textarea
                name="lifeChange"
                placeholder={labels.lifeChange || "What would change in your life if you solved this?"}
                value={formData.lifeChange}
                onChange={handleChange}
              />
            </div>
          )}

          {renderStepDots()}

          <div className={styles.submitContainer}>
            {step < 3 ? (
              <button
                type="button"
                className={styles.submit}
                onClick={() => setStep((prev) => prev + 1)}
                disabled={!canContinue()}
              >
                Continue
              </button>
            ) : (
              <button type="submit" className={styles.submit} disabled={!canContinue()}>
                Book My Free Audit
              </button>
            )}
          </div>
        </form>
      </section>

      {submitted && (
        <div className={styles.thankYouWrapper}>
          <div className={styles.bounceBox}>
            <h3>Thank you!</h3>
            <p>We received your info and will get back to you within 48 hours.</p>
            <a href="#top" className={styles.backToTop}>Back to Top</a>
          </div>
        </div>
      )}
    </>
  );
}

const defaultHelpAreas = [
  "Lead generation",
  "Automation",
  "Sales funnels",
  "CRM / Client management",
  "Social media growth",
  "Paid ads / Scaling",
  "Content creation",
  "Retention / Repeat customers",
  "Other"
];

export default BookingSection;
